/**
 * The app's global Options-API mixin, extracted from main.js so tests can
 * install the same helpers a real component gets (`this.deepCopy`,
 * `this.defaultFunctionsStripHtml`, `this.isSmallDevice`, …).
 *
 * Before this split, specs that mounted a component re-implemented whichever
 * few helpers that component happened to call. That is a fixture that drifts:
 * tests/unit/quizTypeRenderSweep.spec.js mounts 33 components and would have
 * needed a hand-written stand-in for all 27 methods and 12 computeds, each of
 * them free to disagree with the real thing. Import this instead.
 *
 * Phase 6.9 moved the implementations themselves into pure functions in
 * `src/utils/globals.js` + `src/utils/format.js`; this file is only the
 * Options-API surface over them. New `<script setup>` components should use
 * `useGlobals()` from `src/composables/useGlobals.js`.
 */
import * as G from '@/utils/globals'
import * as F from '@/utils/format'

export default {
    computed: {
        defaultErrorMessages: G.defaultErrorMessages,
        defaultValuesCurrentEnvironmentBaseURL: G.currentEnvironmentBaseURL,
        defaultValuesIsProduction: G.isProductionEnv,
        defaultValuesIsStaging: G.isStagingEnv,
        defaultValuesProfilePictureURL: () => G.DEFAULT_PROFILE_PICTURE_URL,
        isExtraLargeDevice: G.isExtraLargeDevice,
        isLargeDevice: G.isLargeDevice,
        isMediumDevice: G.isMediumDevice,
        isSmallDevice: G.isSmallDevice,
        getCDNPrefix: () => G.DEFAULT_CDN_PREFIX,
        getUserLanguage: G.getUserLanguage,
    },
    methods: {
        deepCopy: G.deepCopy,
        formatDateSubmission: F.formatDateSubmission,
        dateAddDays: G.dateAddDays,
        getUserDefaultPicture: G.getUserDefaultPicture,
        transformMessageDate: G.transformMessageDate,
        formatDateAndTime: F.formatDateAndTime,
        formatToDate: G.formatToDate,
        formatUnixTimestampToLL: G.formatUnixTimestampToLL,
        formatDateAndTimeForDatepicker: G.formatDateAndTimeForDatepicker,
        formatDateAndTimeForBackend: G.formatDateAndTimeForBackend,
        getTimestampTime: G.getTimestampTime,
        defaultFunctionsCapitalizeFirstLetter: G.capitalizeFirstLetter,
        defaultFunctionsCopyToClipboard: G.copyToClipboard,
        defaultFunctionsGenerateRandomString: G.generateRandomString,
        defaultFunctionsNewlineToBR: G.newlineToBR,
        defaultFunctionsStripHtml: G.stripHtml,
        defaultFunctionsFormatHtml: G.formatHtml,
        defaultFunctionsRemoveNewlines: G.removeNewlines,
        defaultFunctionsStripYoutubeIdFromURL: G.stripYoutubeIdFromURL,
        defaultFunctionsTestIsUrl: G.testIsUrl,
        defaultFunctionsTestIsEmail: G.testIsEmail,
        getArrayDifference: G.getArrayDifference,
        generateLinkFromURL: G.generateLinkFromURL,
        // The toast spawners need access to `this.$snotify` (a per-instance
        // computed that proxies vue-toastification under the legacy snotify
        // surface). Pass it through to the pure function.
        spawnSuccessToast(message, timeToLive, title) {
            return G.spawnSuccessToast(this.$snotify, message, timeToLive, title)
        },
        spawnErrorToast(message, timeToLive) {
            return G.spawnErrorToast(this.$snotify, message, timeToLive)
        },
        convertDateTimeToUtc: G.convertDateTimeToUtc,
        trackEvent: G.trackEvent,
    }
}
